/**
 * 작성자 : 홍선영
 * 날짜 : 2024.07.03
 * 경로 : 설정 관리 - 태블릿 안전교육 관리 - 안전보건 교육 문제 관리 (현장 관리자),
 *       설정 관리 - 방문자 설정 관리 - 문답 설정
 */

import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import { IUser, userState } from '../atoms';
import { InputTable } from '../assets/styles/InputTable';
import { useSetAuth } from '../utils/useSetAuth';
import { BtnBlue, BtnRed } from './Button';
import InnerTabs from './InnerTabs';
import IssueGuide from './IssueGuide';
import { ButtonsWrapper, Root } from '../pages/sm_setting/setting7/cctvInfoStyle';
import { apiGet, apiPost } from '../services/_common';
import ClickableContainer from '../pages/s_cctv/S_cctv1/RealtimeCCTV/ClickableContainer';
import TableLamp from './TableLamp';
import { IAuth, IModal } from 'customTypes';
import RadioContainer from './button/RadioContainer';
import Input from './Input';
import styled from 'styled-components';
import DetailsLog from '../pages/sm_setting/setting12/DetailsLog';
import { LoadingModalBackground } from '../assets/styles/Modal';
import { PulseLoader } from 'react-spinners';
import { ExpandMenuStyle, MainMenuStyle, SubMenuListStyle } from './cctv/ExpandableMenu';
import ExpandButton from './button/ExpandButton';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { LANGUAGE_TYPES } from '../_constants';
import Portal from './Portal';
import DeleteModal from './Modal/DeleteModal2';
import { scrollToNodeBottom } from '../utils/scrollToNodeBottom';
import { toast } from 'react-toastify';
import { logPost } from '../services/log';
import TextInfoBox from './TextInfoBox';

const INNER_TABS_MAIN = [{ key: 0, name: '문제 문항' }];
const INNER_TABS_SUB = [{ key: 1, name: '보기' }];

const SafeEduEditorStyle = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: fit-content;
  height: 100%;
  overflow: hidden;
  justify-content: flex-start;

  .header {
    padding: 0.75rem;
    height: fit-content;
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    color: var(--text-secondary);
    display: flex;
    gap: 0.5rem;

    .no_bg > div > div > input {
      background-color: transparent;
    }

    .wrap {
      flex-wrap: wrap;
    }

    &:nth-child(n + 2) {
      border-top: 1px solid var(--outline);
    }

    .title {
      flex-shrink: 0;
      width: fit-content;
      min-width: 4rem;
      font-weight: 600;
      margin-right: 0.5rem;
    }
    .flexGrow {
      flex-grow: 1;
    }

    .radioLabel {
      padding: 0.2rem 0;
      cursor: pointer;

      > label {
        user-select: none;
        padding-right: 0.5rem;
        cursor: pointer;
      }
      :hover {
        background-color: var(--tonal-green);
        border-radius: 5px;
      }
    }
  }

  .detailInfo-group {
    margin-top: 1rem;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem 4rem;
  }
  .editor_container {
    display: flex;
    flex-direction: column;
    padding: 1rem;
    overflow: auto;
    margin-top: auto;
    > div > div {
      display: flex;
      flex-direction: row;
      font-size: 0.875rem;
      gap: 1rem;

      label {
        min-width: 6rem;
        display: flex;
        justify-content: flex-start;
      }
    }
  }
  .question_container {
    overflow: scroll;
    flex-grow: 1;
    height: 20vh;

    > div {
      background-color: transparent;
      > div {
        border-top: none;
        padding: 0;
        gap: 0;
        :hover {
          background-color: transparent;
        }
      }
    }

    .question_num {
      padding: 0.5rem;
      font-weight: 500;
      font-size: 0.875rem;
      text-align: center;
      width: 2.5rem;
      border-radius: 0.25rem;
      display: inline-block;
      min-width: 1.5rem;
      text-align: center;
    }

    .mainMenuContainer {
      justify-content: flex-start;
      gap: 0.5rem;
      padding-left: 0.375rem;
      border-top: none;
      align-items: flex-start;

      textarea {
        width: 100%;
        height: 100%;
        resize: none;
        background-color: var(--tonal);
        border-radius: 0.25rem;
        padding: 0.5rem;
        border: none;
        outline: none;
        color: var(--text-primary);

        &:focus {
          outline: 2px solid var(--primary);
          background-color: var(--background);
        }
      }

      .button {
        flex-shrink: 0;
        user-select: none;
        cursor: pointer;
        height: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 0.25rem;
        padding: 0 0.5rem;
        margin: auto 0;
        .material-symbols-rounded {
          font-variation-settings: 'FILL' 1, 'wght' 400;
          font-size: 1.5rem;
        }
      }
      .default-button {
        background-color: var(--tonal);
        color: var(--filled-slate-deep);
        &:hover {
          outline: 0.25rem solid var(--filled-slate);
          &:active {
            transform: scale(0.95);
          }
        }
      }
      .delete-button {
        background-color: var(--tonal-red);
        color: var(--filled-red);
        &:hover {
          outline: 0.25rem solid var(--filled-red);
          &:active {
            transform: scale(0.95);
          }
        }
      }
      .languageType {
        min-width: 5.2rem;
        padding: 0.5rem 0;
      }
    }

    .namespace {
      font-size: 0.85rem;
      margin-left: 0;
    }
  }
  .badge {
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: normal;
    border-radius: 0.25rem;
    height: inherit;
    padding: 0.25rem;
    margin: 0 0.125rem;
    user-select: none;
  }

  .blue {
    color: var(--filled-blue-deep);
    background-color: var(--tonal-blue);
  }

  .green {
    color: var(--filled-green);
    background-color: var(--tonal-green);
  }

  .black {
    color: var(--filled-slate);
    background-color: var(--tonal-deep);
  }

  .violet {
    color: var(--filled-violet-deep);
    background-color: var(--tonal-violet);
  }

  .text-ellipsis {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  .max-28 {
    max-width: 28rem;
  }

  @media screen and (max-width: 767px) {
    .detailInfo-group {
      grid-template-columns: repeat(1, 1fr);
      gap: 0.5rem 4rem;
    }
  }
`;

const INIT_LANGUAGE_YN = [
  { key: 'K', value: true, name: '한국어' },
  { key: 'E', value: false, name: '영어' },
  { key: 'C', value: false, name: '중국어(간체)' },
  { key: 'V', value: false, name: '베트남어' },
  { key: 'T', value: false, name: '태국어' },
  { key: 'M', value: false, name: '미얀마어' },
  { key: 'G', value: false, name: '캄보디아어' },
  { key: 'R', value: false, name: '러시아어' },
  { key: 'I', value: false, name: '인도네시아어' },
  { key: 'Y', value: false, name: '말레이시아어' },
  { key: 'Z', value: false, name: '카자흐스탄어' },
  { key: 'L', value: false, name: '몽골어' },
];

const INIT_EXAM_T: IEduexamT = {
  hCd: null,
  sCd: null,
  mSeq: null,
  tSeq: null,
  dSeq: null,
  kContents: '',
  eContents: '',
  cContents: '',
  vContents: '',
  tContents: '',
  mContents: '',
  gContents: '',
  cdSort: null,
  delYn: 'N',
  writer: null,
  editor: null,
  expand: true,
  languageYn: INIT_LANGUAGE_YN,
};

const EXAM_DTO = {
  hCd: '',
  sCd: '',
  mSeq: null,
  tSeq: null,
  dSeq: null,
  kContents: '',
  eContents: '',
  cContents: '',
  vContents: '',
  tContents: '',
  mContents: '',
  gContents: '',
  rContents: '',
  cdSort: 0,
  delYn: 'N',
  writer: null,
  editor: null,
};

const sortArraybyCd = (array: IEduexamT[]) => {
  const result = array.sort((a: IEduexamT, b: IEduexamT) => {
    const sortA = a.cdSort === null ? Number.MAX_SAFE_INTEGER : a.cdSort;
    const sortB = b.cdSort === null ? Number.MAX_SAFE_INTEGER : b.cdSort;
    return sortA - sortB;
  });

  return result;
};

const updateAnswer = (array: IEduexamT[], tAnsnum: string | null, excludeLanguages?: string[]) => {
  const sorted = sortArraybyCd(array);

  const languageSetKeys = Object.keys(LANGUAGE_TYPES).filter((k) => !excludeLanguages?.includes(k));
  const result = sorted.map((item: IEduexamT, i: number) => {
    const languageYn = languageSetKeys.map((lan: string) => {
      const languageLower = lan.toLocaleLowerCase();
      const key = lan as keyof typeof LANGUAGE_TYPES;
      return { key: lan, value: !!item[`${languageLower}Contents`], name: LANGUAGE_TYPES[key] };
    });
    const isAnswer = i === Number(tAnsnum);
    return { ...item, expand: false, tAnsYn: isAnswer, languageYn };
  });

  return result;
};

const SafeEduExamSetting = ({ examType, excludeLanguages }: { examType: 'SAFE_EDU' | 'VISITOR'; excludeLanguages?: string[] }) => {
  const INIT_EXAM_M: IEduexamM | IVisitExamM = {
    hCd: '',
    sCd: '',
    mSeq: null,
    mTitle: '',
    tTitle: '',
    bigo: '',
    useYn: true,
    tAnsnum: null,
    delYn: 'N',
    wDate: null,
    eDate: null,
    writer: null,
    editor: null,
    ...(examType === 'SAFE_EDU' ? { postEduexamTDto: [EXAM_DTO] } : { postVisitexamTDto: [EXAM_DTO] }),
  };
  const { t } = useTranslation();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const size = useOutletContext<any>();
  const userInfo = useRecoilValue(userState);
  const [viewTable, setViewTable] = useState<boolean>(true);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null); // 문제문항 선택인덱스
  const [isNewAdd, setIsNewAdd] = useState(true); // 신규등록 여부
  const [activeTab, setActiveTab] = useState(0);
  const [selectedQuestionM, setSelectedQuestionM] = useState<IEduexamM>(INIT_EXAM_M);
  const [selectedQuestionT, setSelectedQuestionT] = useState<IEduexamT | null>(null);
  const filteredLanguageYn = excludeLanguages ? INIT_LANGUAGE_YN.filter((l) => !excludeLanguages.includes(l.key)) : INIT_LANGUAGE_YN;
  const [requiredLanguage, setRequiredLanguage] = useState(filteredLanguageYn);

  const reqInfo =
    examType === 'SAFE_EDU'
      ? { path: '/eduexam', pathM: '/eduexam/m', pathT: '/eduexam/t', pathD: '/eduexam/d', dtoKeyT: 'postEduexamTDto', dtoKeyD: 'postEduexamDDto' } // 근로자 안전교육 문제
      : { path: '/visit/exam', pathM: '/visit/exam/m', pathT: '/visit/exam/t', pathD: '/visit/exam/d', dtoKeyT: 'postVisitexamTDto', dtoKeyD: 'postVisitexamDDto' }; // 방문자 안전교육 문제

  const {
    data: mList,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery(['safeEduExamMGet', userInfo.hCd, userInfo.sCd], () => fetchDataM(), {
    enabled: !!userInfo.hCd && !!userInfo.sCd,
  });

  const [questionMList, setQuestionMList] = useState<IEduexamM[]>(mList || []);

  const { data: tList, refetch: refetchTList } = useQuery(['safeEduExamTGet', userInfo.hCd, userInfo.sCd, selectedQuestionM?.mSeq], () => fetchDataT(), {
    enabled: !!userInfo.hCd && !!userInfo.sCd && !!selectedQuestionM && !!selectedQuestionM.mSeq,
  });

  const init = { ...INIT_EXAM_T, hCd: userInfo.hCd, sCd: userInfo.sCd, writer: userInfo.userId, editor: userInfo.userId, cdSort: 0, languageYn: filteredLanguageYn };
  const [questionTList, setQuestionTList] = useState<IEduexamT[]>(tList || [init]);

  const { data: dList } = useQuery(['safeEduExamDGet', userInfo.hCd, userInfo.sCd, selectedQuestionM?.mSeq, selectedQuestionT], () => fetchDataD(), {
    enabled: !!userInfo.hCd && !!userInfo.sCd && !!selectedQuestionM && !!selectedQuestionM.mSeq && !!selectedQuestionT && !!selectedQuestionT.tSeq,
  });

  useEffect(() => {
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: examType === 'VISITOR' ? '방문자 설정 관리 > 문답 설정' : '태블릿 안전교육 관리 > 안전보건 교육 문제 관리',
      action: '조회',
      etc: ``,
    });
  }, [examType]);

  // 문제 목록
  const fetchDataM = async () => {
    try {
      const res = await apiGet({ path: reqInfo.pathM, req: { hCd: userInfo.hCd, sCd: userInfo.sCd } });
      const result = res.data.data.map((el: any) => (el.useYn === 'Y' ? { ...el, useYn: true } : { ...el, useYn: false }));
      setQuestionMList(result);
      return result;
    } catch (error) {
      console.error('error', error);
      throw new Error('error');
    }
  };

  // 문제 문항(문제)
  const fetchDataT = async () => {
    try {
      const res = await apiGet({ path: reqInfo.pathT, req: { hCd: userInfo.hCd, sCd: userInfo.sCd, mSeq: selectedQuestionM?.mSeq } });
      const result = res.data.data.filter((el: IEduexamM) => el.delYn !== 'Y');
      const newArray = updateAnswer(result, '', excludeLanguages);
      setQuestionTList(newArray);
      return result;
    } catch (error) {
      console.error('error', error);
      throw new Error('error');
    }
  };

  // 문제 항목(객관식보기)
  const fetchDataD = async () => {
    try {
      const res = await apiGet({ path: reqInfo.pathD, req: { hCd: userInfo.hCd, sCd: userInfo.sCd, mSeq: selectedQuestionM?.mSeq, tSeq: selectedQuestionT?.tSeq } });
      const result = res.data.data.filter((el: IEduexamT) => el.delYn !== 'Y');
      if (result.length > 0) return result;
      return [init];
    } catch (error) {
      console.error('error', error);
      throw new Error('error');
    }
  };

  const addBtnAction = () => {
    setIsNewAdd(true);
    setSelectedIndex(null);
    setSelectedQuestionM(INIT_EXAM_M);
    setQuestionTList([init]);
    if (size.innerSize.W <= 1024) setViewTable(false);
  };

  const backToMain = () => {
    setViewTable(true);
    if (activeTab !== 0) {
      setActiveTab(0);
    } else {
      setSelectedIndex(null);
      setSelectedQuestionT(null);
      setSelectedQuestionM(INIT_EXAM_M);
      setQuestionTList([init]);
      setIsNewAdd(true);
    }
  };

  const updateUseYn = async (array: IEduexamM[], useYnString: 'Y' | 'N', msg: boolean) => {
    const updateNewY = array.find((el) => el.useYn);
    if (updateNewY) {
      const { wDate, eDate, ...rest } = updateNewY;
      const questionT = { ...rest, hCd: userInfo.hCd, sCd: userInfo.sCd, useYn: useYnString };
      const req = { ...questionT, [reqInfo.dtoKeyT]: [] };
      const res = await apiPost({ path: reqInfo.path, contentType: 'application/json', req });
      const { statusCode, message } = res.data;
      if (statusCode === 200) {
        toast.success(t('사용유무가 업데이트 되었습니다'));
        logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: examType === 'VISITOR' ? '방문자 설정 관리 > 문답 설정' : '태블릿 안전교육 관리 > 안전보건 교육 문제 관리',
          action: '저장',
          etc: ``,
        });
      }
    }
  };

  const handleUseYnClick = async (e: React.MouseEvent<HTMLDivElement>, check: boolean, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    const updatedCheckValue = !check;

    if (updatedCheckValue) {
      const newArray = questionMList.map((el, i) => (i === index ? { ...el, useYn: updatedCheckValue } : { ...el, useYn: !updatedCheckValue }));
      setQuestionMList(newArray);

      updateUseYn(questionMList, 'N', false); // 기존 사용중인 문제관리를 N으로 업데이트
      updateUseYn(newArray, 'Y', true); // 새로 사용등록한 문제관리 Y 업데이트
    } else {
      const newArray = questionMList.map((el, i) => (i === index ? { ...el, useYn: updatedCheckValue } : el));
      setQuestionMList(newArray);

      updateUseYn(questionMList, 'N', true); // 미사용클릭시 N 업데이트
    }
  };

  const onClickEduExamM = (index: number | null, mObj: IEduexamM | null) => {
    if (index !== null) {
      setSelectedIndex(index);
      setIsNewAdd(false);
    }
    if (mObj) setSelectedQuestionM(mObj);
  };

  if (isError) return <IssueGuide />;

  if (isLoading || isFetching)
    return (
      <LoadingModalBackground>
        <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
      </LoadingModalBackground>
    );

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={`content-container ${size.innerSize.W > 1024 ? 'twoColumn column-55 max800' : 'oneColumn'}`}>
        <Root className={size.innerSize.W > 1024 || viewTable ? 'showRoot' : 'hideRoot'}>
          {selectedIndex !== null ? (
            <>
              <InnerTabs visibleBackBtn tabList={INNER_TABS_MAIN} activeTab={activeTab} backToMain={backToMain} backBtnPlacement='right' />
              {activeTab === 0 && (
                <EduExamEditor
                  key='main'
                  type='main'
                  userInfo={userInfo}
                  questionRows={questionTList}
                  setQuestionRows={setQuestionTList}
                  auth={auth}
                  isNewAdd={isNewAdd}
                  setIsNewAdd={setIsNewAdd}
                  selectedQuestionM={selectedQuestionM}
                  setSelectedQuestionM={setSelectedQuestionM}
                  selectedQuestionT={selectedQuestionT}
                  setSelectedQuestionT={setSelectedQuestionT}
                  questionMList={questionMList}
                  backToMain={backToMain}
                  refetchMList={refetch}
                  requiredLanguage={requiredLanguage}
                  setRequiredLanguage={setRequiredLanguage}
                  setViewTable={setViewTable}
                  setActiveTab={setActiveTab}
                  examType={examType}
                  reqInfo={reqInfo}
                  excludeLanguages={excludeLanguages}
                />
              )}
            </>
          ) : (
            <>
              <InputTable className='margin-left-05 noHorizontalScroll'>
                <div className='thead'>
                  <div className='tr'>
                    <div className='trCol2p5 flex-center shrink0'>No</div>
                    <div className='trColGrow flex-center shrink1'>{t('문제 관리명')}</div>
                    <div className='trCol6 flex-center shrink1'>{t('사용유무')}</div>
                  </div>
                </div>
                {questionMList.length > 0 ? (
                  <div className='table'>
                    <div className='tbody'>
                      {questionMList?.map((el, i) => (
                        <div key={i} className={`tr ${selectedIndex !== null && i === selectedIndex ? 'selected' : ''}`} role='presentation' onClick={() => onClickEduExamM(i, el)}>
                          <div className='trCol2p5 flex-center shrink0'>{i + 1}</div>
                          <div className='trColGrow flex-basic shrink1 text-ellipsis max-28'>{el.mTitle}</div>
                          <div className='trCol6 flex-center'>
                            <ClickableContainer
                              onClick={(e: any) => handleUseYnClick(e, el.useYn, i)} //
                              button={<TableLamp use={el.useYn} />}
                              disabled={!auth.updateAuth}
                              shouldPreventBubble
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <IssueGuide text='문제 관리 목록이 없습니다' />
                )}
              </InputTable>
              <ButtonsWrapper>{(auth.createAuth || auth.updateAuth) && <BtnBlue onClick={() => addBtnAction()}>{t('새 문제 관리 작성')}</BtnBlue>}</ButtonsWrapper>
            </>
          )}
        </Root>
        {(size.innerSize.W > 1024 || !viewTable) && (
          <Root>
            <InnerTabs visibleBackBtn={false} tabList={selectedIndex === null ? INNER_TABS_MAIN : INNER_TABS_SUB} activeTab={selectedIndex === null ? 0 : 1} backToMain={backToMain} />
            {selectedIndex !== null ? (
              selectedQuestionT !== null ? (
                <EduExamEditor
                  key='sub'
                  type='sub'
                  userInfo={userInfo}
                  questionRows={dList || [init]}
                  auth={auth}
                  isNewAdd={isNewAdd}
                  setIsNewAdd={setIsNewAdd}
                  selectedQuestionM={selectedQuestionM}
                  setSelectedQuestionM={setSelectedQuestionM}
                  selectedQuestionT={selectedQuestionT}
                  setSelectedQuestionT={setSelectedQuestionT}
                  refetchTList={refetchTList}
                  setViewTable={setViewTable}
                  setActiveTab={setActiveTab}
                  examType={examType}
                  reqInfo={reqInfo}
                  excludeLanguages={excludeLanguages}
                />
              ) : (
                <IssueGuide text='문제 문항을 선택하세요' type='select' />
              )
            ) : (
              <EduExamEditor
                key='main'
                type='main'
                userInfo={userInfo}
                questionRows={questionTList}
                setQuestionRows={setQuestionTList}
                auth={auth}
                isNewAdd={isNewAdd}
                setIsNewAdd={setIsNewAdd}
                selectedQuestionM={selectedQuestionM}
                setSelectedQuestionM={setSelectedQuestionM}
                selectedQuestionT={selectedQuestionT}
                setSelectedQuestionT={setSelectedQuestionT}
                questionMList={questionMList}
                backToMain={backToMain}
                refetchMList={refetch}
                requiredLanguage={requiredLanguage}
                setRequiredLanguage={setRequiredLanguage}
                refetchTList={refetchTList}
                setViewTable={setViewTable}
                setActiveTab={setActiveTab}
                examType={examType}
                reqInfo={reqInfo}
                excludeLanguages={excludeLanguages}
              />
            )}
          </Root>
        )}
      </div>
    </DndProvider>
  );
};

interface IEduExamEditor {
  questionRows: any;
  setQuestionRows?: Dispatch<SetStateAction<any>>;
  auth: IAuth;
  isNewAdd: boolean;
  setIsNewAdd: Dispatch<SetStateAction<boolean>>;
  type: 'main' | 'sub';
  selectedQuestionM: IEduexamM;
  setSelectedQuestionM: Dispatch<SetStateAction<IEduexamM>>;
  userInfo: IUser;
  selectedQuestionT?: IEduexamT | null;
  setSelectedQuestionT?: Dispatch<SetStateAction<IEduexamT | null>>;
  questionMList?: IEduexamM[];
  backToMain?: () => void;
  refetchMList?: () => void;
  refetchTList?: () => void;
  requiredLanguage?: IlanYn[];
  setRequiredLanguage?: Dispatch<SetStateAction<IlanYn[]>>;
  setActiveTab: Dispatch<SetStateAction<number>>;
  setViewTable: Dispatch<SetStateAction<boolean>>;
  examType: 'SAFE_EDU' | 'VISITOR';
  reqInfo: { path: string; pathM: string; pathT: string; pathD: string; dtoKeyT: string; dtoKeyD: string };
  excludeLanguages?: string[];
}

const EduExamEditor = ({
  questionRows,
  setQuestionRows,
  auth,
  isNewAdd,
  setIsNewAdd,
  type,
  selectedQuestionM,
  setSelectedQuestionM,
  userInfo,
  selectedQuestionT,
  setSelectedQuestionT,
  questionMList,
  backToMain,
  refetchMList,
  refetchTList,
  requiredLanguage,
  setRequiredLanguage,
  setActiveTab,
  setViewTable,
  examType,
  reqInfo,
  excludeLanguages,
}: IEduExamEditor) => {
  const { t } = useTranslation();
  const [useYn, setUseYn] = useState(false);
  const [selectedQuestionD, setSelectedQuestionD] = useState<IEduexamT[]>(questionRows || INIT_EXAM_T);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', title: '' });
  const size = useOutletContext<any>();
  const [isExpandedExplanation, setIsExpandedExplanation] = useState(true);
  const [explain, setExplain] = useState<Record<string, string>>({
    kExplain: '',
    eExplain: '',
    cExplain: '',
    vExplain: '',
    tExplain: '',
    mExplain: '',
    gExplain: '',
    rExplain: '',
  });

  useEffect(() => {
    selectedQuestionM && updateQuestionD(questionRows);
    if (questionRows) {
      if (questionRows[0]?.languageYn) setRequiredLanguage && setRequiredLanguage(questionRows[0]?.languageYn);
    }
  }, [questionRows]);

  // 문제문항을 선택을 변경한 경우
  useEffect(() => {
    if (selectedQuestionM) {
      setUseYn(selectedQuestionM.useYn);
      if (selectedQuestionT) {
        setIsNewAdd(false);
        setSelectedQuestionM((prev: IEduexamM) => ({ ...prev, tTitle: selectedQuestionT.kContents, languageYn: selectedQuestionT.languageYn }));
      }
    }
  }, [selectedQuestionT && selectedQuestionT.tSeq]);

  useEffect(() => {
    if (selectedQuestionT) {
      setExplain({
        kExplain: selectedQuestionT.kExplain || '',
        eExplain: selectedQuestionT.eExplain || '',
        cExplain: selectedQuestionT.cExplain || '',
        vExplain: selectedQuestionT.vExplain || '',
        tExplain: selectedQuestionT.tExplain || '',
        mExplain: selectedQuestionT.mExplain || '',
        gExplain: selectedQuestionT.gExplain || '',
        rExplain: selectedQuestionT.rExplain || '',
      });
    }
  }, [selectedQuestionT]);

  const updateQuestionD = (array: IEduexamT[]) => {
    const newArray = updateAnswer(array, selectedQuestionT?.tAnsnum || null, excludeLanguages);
    setSelectedQuestionD(newArray);
  };

  useEffect(() => {
    if (isNewAdd) initiateState();
  }, [isNewAdd]);

  const initiateState = () => {
    setUseYn(false);
    // setSelectedQuestionM(INIT_EDUEXAM_T);
  };

  const onClickAddNewQuestion = () => {
    if (type === 'main' && selectedQuestionD.length >= 10) {
      toast.warning(t('최대 10문항까지 작성 가능합니다.'));
      return;
    }
    if (type === 'sub' && selectedQuestionD.length >= 4) {
      toast.warning(t('최대 4항목까지 작성 가능합니다.'));
      return;
    }

    const updateLanguage = { ...INIT_EXAM_T, languageYn: requiredLanguage };
    const newObj = { ...updateLanguage, cdSort: selectedQuestionD.length, writer: userInfo.userId, editor: userInfo.userId, mSeq: selectedQuestionM.mSeq };
    // const newObj = { ...INIT_EXAM_T, cdSort: selectedQuestionD.length, writer: userInfo.userId, editor: userInfo.userId, mSeq: selectedQuestionM.mSeq, tSeq: tSeq?.tSeq || null };
    setSelectedQuestionD((prev) => [...prev, newObj]);
    scrollToNodeBottom(scrollContainerRef);
  };

  const onClickQuestionRow = (tRow: IEduexamT) => {
    if (type === 'main' && tRow && tRow.mSeq && tRow.tSeq) {
      const newTRow = { ...tRow, languageYn: requiredLanguage };
      setSelectedQuestionT && setSelectedQuestionT(newTRow);

      if (size.innerSize.W <= 1024) {
        setActiveTab(1);
        setViewTable(false);
      }
    }
  };

  const onClickSaveExam = async () => {
    if (!selectedQuestionM) return;
    if (selectedQuestionM.mTitle?.trim() === '') {
      toast.warning(t('제목을 입력하세요'));
      return;
    }
    if (selectedQuestionD.find((el) => el.kContents?.trim() === '')) {
      toast.warning(t('한국어 항목을 입력하세요'));
      return;
    }

    const newQuestionT = {
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      mSeq: selectedQuestionM?.mSeq,
      mTitle: selectedQuestionM.mTitle,
      bigo: selectedQuestionM.bigo,
      useYn: useYn ? 'Y' : 'N',
      delYn: selectedQuestionM.delYn,
      writer: selectedQuestionM?.mSeq ? selectedQuestionM.writer : userInfo.userId,
      editor: userInfo.userId,
    };

    if (type === 'main') {
      const languageInputFilled = validateContents(requiredLanguage, selectedQuestionD);
      if (!languageInputFilled) {
        toast.warning(t('필수 언어 항목을 모두 입력하세요'));
        return;
      }

      // 수정중인 기존 문제관리 또는 신규 작성중인 문제관리의 사용유무가 'Y'인 경우
      // 다른 문제관리의 사용유무를 'N'으로 변경
      // 목록중 1개만 사용중이어야 함
      if (useYn) {
        const hasY = questionMList?.find((el) => el.useYn);
        if (hasY) {
          const { wDate, eDate, ...rest } = hasY;
          const questionT = { ...rest, hCd: userInfo.hCd, sCd: userInfo.sCd, useYn: 'N' };
          const req = { ...questionT, [reqInfo.dtoKeyT]: [] };
          const res = await apiPost({ path: reqInfo.path, contentType: 'application/json', req });
          const { statusCode, message } = res.data;
          if (statusCode === 200) {
            toast.success(t(message));
            logPost({
              hCd: userInfo.hCd,
              sCd: userInfo.sCd,
              userId: userInfo.userId,
              menu: examType === 'VISITOR' ? '방문자 설정 관리 > 문답 설정' : '태블릿 안전교육 관리 > 안전보건 교육 문제 관리',
              action: '저장',
              etc: ``,
            });
          }
        }
      }

      // 필수입력언어 체크 해제한 컨텐츠값 ''로 업데이트
      const newRequiredLan = requiredLanguage?.filter((el: IlanYn) => !el.value);
      const removeUnrequiredLang = selectedQuestionD.map((el: IEduexamT) => {
        newRequiredLan?.forEach((lan: IlanYn) => {
          const contentKey = `${lan.key.toLowerCase()}Contents`;
          el[contentKey] = ''; // 선택되지 않은 언어 ''로 초기화
        });
        return el;
      });

      const newQuestionD = removeUnrequiredLang.map(({ expand, wDate, eDate, tAnsYn, tAnsnum, languageYn, ...rest }) => ({
        ...rest,
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        mSeq: selectedQuestionM?.mSeq,
      }));

      const sorted = sortArraybyCd(newQuestionD); // cdSort 오름차순으로 정렬
      const reSortedQuestionD = sorted.map((el, i) => {
        // explain 값 제거
        const { dSeq, kExplain, eExplain, cExplain, vExplain, tExplain, mExplain, gExplain, rExplain, ...rest } = el;
        return {
          ...rest,
          cdSort: i,
        };
      }); // cdSort 빈값을 채우기
      let req: any = { ...newQuestionT, [reqInfo.dtoKeyT]: reSortedQuestionD };
      if (examType === 'VISITOR') {
        req = {
          ...req,
          mSeq: req.mSeq || 0,
          [reqInfo.dtoKeyT]: reSortedQuestionD.map((el) => {
            const { mSeq, tSeq, ...rest } = el;
            return {
              ...rest,
              mSeq: mSeq || 0,
              tSeq: tSeq || 0,
            };
          }),
        };
      }
      const res = await apiPost({ path: reqInfo.path, req, contentType: 'application/json' });
      const { statusCode, message } = res.data;
      if (statusCode === 200) {
        let examMs = [];
        let examTs = [];
        if (examType === 'SAFE_EDU') {
          examMs = res.data.data.pmsEduexamMs;
          examTs = res.data.data.pmsEduexamTs;
        } else {
          examMs = res.data.data.pmsVisitexamMs;
          examTs = res.data.data.pmsVisitexamTs;
        }

        const result = examTs.filter((item: IEduexamT) => item.delYn !== 'Y');

        setQuestionRows && setQuestionRows(result);
        refetchMList && refetchMList();
        const { eDate, editor, writer, wDate, mSeq } = examMs[0];
        const { delYn, hCd, sCd, mTitle, bigo } = selectedQuestionM;

        const baseObject = {
          hCd,
          sCd,
          bigo,
          delYn,
          useYn,
          writer,
          editor,
          wDate,
          eDate,
          mTitle,
          tAnsnum: null,
          languageYn: requiredLanguage,
        };
        if (!selectedQuestionM.mSeq) {
          setSelectedQuestionM({ ...baseObject, mSeq });
        } else {
          setSelectedQuestionM({ ...baseObject, mSeq: selectedQuestionM.mSeq });
        }
        setIsNewAdd(false);
        toast.success(t(message));
        logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: examType === 'VISITOR' ? '방문자 설정 관리 > 문답 설정 (문제 문항)' : '태블릿 안전교육 관리 > 안전보건 교육 문제 관리 (문제 문항)',
          action: '저장/수정',
          etc: ``,
        });
      }
    } else {
      const languageInputFilled = validateContents(selectedQuestionM?.languageYn, selectedQuestionD);
      // examType === 'VISITOR' 인 경우 해설 필수 입력 언어 체크
      const explanationInputFilled = examType === 'VISITOR' ? validateExplanation(selectedQuestionM?.languageYn, explain) : true;
      if (!languageInputFilled) {
        toast.warning(t('필수 언어 항목을 모두 입력하세요'));
        return;
      }
      if (!explanationInputFilled) {
        toast.warning(t('해설 필수 입력 언어를 모두 입력하세요'));
        return;
      }

      const sorted = sortArraybyCd(selectedQuestionD); // cdSort 오름차순으로 정렬
      const reSortedQuestionD = sorted.map((el, i) => ({ ...el, cdSort: i })); // cdSort 빈값을 채우기

      const newQuestionD = reSortedQuestionD.map(({ expand, wDate, eDate, tAnsYn, languageYn, ...rest }) => ({
        ...rest,
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        mSeq: selectedQuestionM?.mSeq,
        tSeq: selectedQuestionT?.tSeq,
      }));

      const ansNum = reSortedQuestionD.findIndex((item) => item.tAnsYn);
      if (ansNum !== -1) {
        const ansNumToString = ansNum.toString();
        let req: any = { tAnsnum: ansNumToString, [reqInfo.dtoKeyD]: newQuestionD };
        if (examType === 'VISITOR') {
          req = {
            ...req,
            ...explain,
            [reqInfo.dtoKeyD]: newQuestionD.map((el, i) => {
              const { dSeq, ...rest } = el;
              return {
                ...rest,
                dSeq: dSeq || 0,
              };
            }),
          };
        }
        const res = await apiPost({ path: reqInfo.pathD, req, contentType: 'application/json' });
        const { statusCode, message } = res.data;
        if (statusCode === 200) {
          refetchTList && refetchTList();
          const newArray = updateAnswer(res.data.data, ansNumToString, excludeLanguages);
          setSelectedQuestionD(newArray);
          toast.success(t(message));
          logPost({
            hCd: userInfo.hCd,
            sCd: userInfo.sCd,
            userId: userInfo.userId,
            menu: examType === 'VISITOR' ? '방문자 설정 관리 > 문답 설정 (문제 항목)' : '태블릿 안전교육 관리 > 안전보건 교육 문제 관리 (문제 항목)',
            action: '저장/수정',
            etc: ``,
          });
        }
      } else toast.warning(t('정답 항목을 선택하세요'));
    }
  };

  const onClickDeleteExamM = () => {
    setOpenModal((prev) => ({ ...prev, status: true, type: 'delete', api: () => deleteAPI() }));
  };

  const deleteAPI = async () => {
    const newQuestionT = {
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      mSeq: selectedQuestionM.mSeq,
      mTitle: selectedQuestionM.mTitle,
      bigo: selectedQuestionM.bigo,
      useYn: selectedQuestionM?.useYn ? 'Y' : 'N',
      delYn: 'Y',
      writer: selectedQuestionM.writer,
      editor: userInfo.userId,
    };
    // explain 값 제거
    const newQuestionD = selectedQuestionD.map(({ expand, wDate, eDate, tAnsYn, tAnsnum, languageYn, kExplain, eExplain, cExplain, vExplain, tExplain, mExplain, gExplain, rExplain, ...rest }) => ({
      ...rest,
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      mSeq: selectedQuestionM?.mSeq,
    }));
    const req = { ...newQuestionT, [reqInfo.dtoKeyT]: newQuestionD };
    const res = await apiPost({ path: reqInfo.path, contentType: 'application/json', req });

    const { statusCode, message } = res.data;
    if (statusCode === 200) {
      let examTs = [];
      if (examType === 'SAFE_EDU') examTs = res.data.data.pmsEduexamTs;
      else examTs = res.data.data.pmsVisitexamTs;

      const result = examTs.filter((item: IEduexamT) => item.delYn !== 'Y');
      setIsNewAdd(true);
      updateQuestionD(result);
      refetchMList && refetchMList();
      toast.success(t(message));
      backToMain && backToMain();
      logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: examType === 'VISITOR' ? '방문자 설정 관리 > 문답 설정' : '태블릿 안전교육 관리 > 안전보건 교육 문제 관리',
        action: '삭제',
        etc: ``,
      });
    }
  };

  // 필수언어 입력값을 모두 입력했는 지 확인
  const validateContents = (languageYn: IlanYn[] | undefined, entries: any) => {
    if (languageYn) {
      return languageYn.every(({ key, value }) => {
        if (value) {
          // 해당 언어가 필수인 경우
          // `entries` 배열의 모든 항목에 대해 해당 언어의 콘텐츠가 비어있지 않은지 확인
          return entries.every((entry: IEduexamT) => {
            const contentKey = `${key.toLowerCase()}Contents`;
            return entry[contentKey]; // 콘텐츠가 비어 있지 않은지 확인
          });
        }
        // 해당 언어가 필수가 아닌 경우, 자동으로 true를 반환하여 검증을 통과
        return true;
      });
    }
    // `languageYn` 배열이 존재하지 않는 경우, false를 반환
    return false;
  };

  const validateExplanation = (languageYn: IlanYn[] | undefined, _explain: Record<string, string>) => {
    if (languageYn) {
      return languageYn.every(({ key, value }) => {
        if (value) {
          return _explain[`${key.toLocaleLowerCase()}Explain`] && _explain[`${key.toLocaleLowerCase()}Explain`].trim() !== '';
        }
        return true;
      });
    }
    return false;
  };

  const handleUseYnClick = (e: React.MouseEvent<HTMLDivElement>, check: boolean, i: number) => {
    if (requiredLanguage) {
      const updatedCheckValue = !check;
      const copyArray = [...requiredLanguage];
      const newArray = copyArray.map((el, index) => {
        return index === i && el.key !== 'K' ? { ...el, value: updatedCheckValue } : el;
      });
      setRequiredLanguage && setRequiredLanguage(newArray);
    }
  };

  const onClickExpandExplanation = () => {
    setIsExpandedExplanation(!isExpandedExplanation);
  };

  const onChangeExplanation = (e: React.ChangeEvent<HTMLTextAreaElement>, tGubun: string) => {
    setExplain((prev) => ({ ...prev, [`${tGubun.toLocaleLowerCase()}Explain`]: e.target.value }));
  };

  return (
    <SafeEduEditorStyle>
      <div className='header'>
        <span className='title required'>{type === 'main' ? t('문제 관리명') : t('질문')}</span>
        <div className={`flexGrow ${type === 'sub' ? 'no_bg' : ''}`}>
          <Input
            type='text'
            id={type === 'main' ? 'mTitle' : 'tTitle'}
            name={type === 'main' ? 'mTitle' : 'tTitle'}
            state={selectedQuestionM}
            setState={setSelectedQuestionM}
            disabled={(!auth.createAuth && !auth.updateAuth) || type === 'sub'}
            maxLength={100}
          />
        </div>
        {type === 'main' && (
          <>
            <RadioContainer name={t('사용')} onClick={() => setUseYn(true)} isSelected={useYn} />
            <RadioContainer name={t('미사용')} onClick={() => setUseYn(false)} isSelected={!useYn} />
          </>
        )}
      </div>
      {type === 'main' && (
        <div className='header'>
          <span className='title required'>{t('필수 입력 언어')}</span>
          <div className='flex-basic wrap'>
            {type === 'main' &&
              requiredLanguage?.map((lan: IlanYn, i: number) => (
                <div className='flex-basic radioLabel' key={lan.key} role='presentation' onClick={(e) => handleUseYnClick(e, lan.value, i)}>
                  <ClickableContainer
                    className='padding-05'
                    disabled={lan.key === 'K'}
                    onClick={(e: any) => e.preventDefault()} //
                    button={<TableLamp use={lan.value} />}
                  />
                  <label htmlFor={lan.key}>{t(lan.name)}</label>
                </div>
              ))}
          </div>
        </div>
      )}
      <div className='flex-between' style={{ margin: '0.5rem' }}>
        <div>
          <span className='badge black'>
            {t('최대')} {type === 'main' ? '10' : '4'} {t('문항')}
          </span>
        </div>
        {type === 'sub' && (
          <div className='flex-basic'>
            <div>
              <span className='badge black'>{t('필수입력 언어')}</span>
            </div>
            <div>
              {selectedQuestionM.languageYn
                ?.filter((item) => item.value)
                ?.map((item2) => (
                  <span className={`badge ${item2.key === 'K' ? 'blue' : 'violet'}`} key={item2.key}>
                    {t(item2.name)}
                  </span>
                ))}
            </div>
          </div>
        )}
      </div>
      <TextInfoBox ico='flash_on' description='보기 문항 드래그 앤 드롭을 지원합니다. 행의 앞부분을 끌어 순서를 위, 아래로 변경 후 저장버튼을 클릭하세요' color='orange' />
      <div className='question_container' ref={scrollContainerRef}>
        {selectedQuestionD.map((el, i) => (
          <DraggableRow
            key={`${type}_${el.cdSort}_${i}`}
            el={el}
            index={i}
            tableState={selectedQuestionD}
            setTableState={setSelectedQuestionD}
            selectedQuestionM={selectedQuestionM}
            setSelectedQuestionM={setSelectedQuestionM}
            selectedQuestionT={selectedQuestionT}
            setSelectedQuestionT={setSelectedQuestionT}
            auth={auth}
            moveRow={dragRow}
            onClickQuestionRow={() => {
              if (type === 'main') return onClickQuestionRow(el);
              return null;
            }}
            type={type}
            requiredLanguage={type === 'main' ? requiredLanguage : selectedQuestionT?.languageYn}
            refetchTList={refetchTList}
            examType={examType}
            reqInfo={reqInfo}
            excludeLanguages={excludeLanguages}
            explain={explain}
          />
        ))}
      </div>
      {type === 'sub' && examType === 'VISITOR' && (
        <div className='question_container'>
          {/** 해설 문항 추가, 다국어 포함 추가 */}
          <div className='flex-between' style={{ margin: '0.5rem' }}>
            <div>
              <span className='badge black'>{t('정답 해설')}</span>
            </div>
          </div>
          {/** 해설 문항 추가, 다국어 포함 추가 */}
          <ExpandMenuStyle>
            <MainMenuStyle>
              <ExpandButton isExpanded={isExpandedExplanation} onClickExpandButton={() => onClickExpandExplanation()} className='margin-05' />
              <ExplanationRow
                tGubun='K'
                languageType='한국어'
                tContents={explain.kExplain || ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChangeExplanation(e, 'K')}
                paddingLeft='3.35rem'
              />
            </MainMenuStyle>
          </ExpandMenuStyle>
          {isExpandedExplanation && (
            <div className='flexColumn' style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {selectedQuestionM.languageYn
                ?.filter((lan: IlanYn) => lan.value && lan.key !== 'K')
                ?.map((lan: IlanYn) => (
                  <ExplanationRow
                    key={lan.key}
                    tGubun={lan.key}
                    languageType={lan.name}
                    tContents={explain[`${lan.key.toLocaleLowerCase()}Explain`] || ''}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChangeExplanation(e, lan.key)}
                    paddingLeft='3.35rem'
                  />
                ))}
            </div>
          )}
        </div>
      )}

      {type === 'main' && (
        <div className='editor_container'>
          <Input label={t('비고')} type='text' id='bigo' name='bigo' state={selectedQuestionM} setState={setSelectedQuestionM} disabled={!auth.updateAuth} maxLength={100} />
          {selectedQuestionM && (
            <div className='detailInfo-group'>
              <DetailsLog name={t('등록일자')} value={selectedQuestionM.wDate || ''} />
              <DetailsLog name={t('등록자')} value={selectedQuestionM.writer || ''} />
              <DetailsLog name={t('수정일자')} value={selectedQuestionM.eDate || ''} />
              <DetailsLog name={t('수정자')} value={selectedQuestionM.editor || ''} />
            </div>
          )}
        </div>
      )}
      <ButtonsWrapper style={{ justifyContent: 'space-between' }}>
        <div>
          <BtnBlue disabled={type === 'sub' && isNewAdd} onClick={onClickAddNewQuestion}>
            {type === 'main' ? t('새 문항 추가') : t('새 보기 추가')}
          </BtnBlue>
        </div>
        <div className='flex-between'>
          {auth.deleteAuth && type === 'main' && (
            <BtnRed onClick={onClickDeleteExamM} disabled={isNewAdd}>
              {t('삭제')}
            </BtnRed>
          )}
          {(auth.createAuth || auth.updateAuth) && <BtnBlue onClick={onClickSaveExam}>{t('저장')}</BtnBlue>}
        </div>
      </ButtonsWrapper>
      {openModal.status && (
        <Portal openModal={openModal.status}>
          <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />
        </Portal>
      )}
    </SafeEduEditorStyle>
  );
};

const DraggableRow = ({
  el,
  index,
  tableState,
  setTableState,
  selectedQuestionM,
  setSelectedQuestionM,
  selectedQuestionT,
  setSelectedQuestionT,
  auth,
  moveRow,
  onClickQuestionRow,
  type,
  requiredLanguage,
  refetchTList,
  examType,
  reqInfo,
  excludeLanguages,
  explain,
}: any) => {
  const trRef = useRef(null);
  const { t } = useTranslation();
  const userInfo = useRecoilValue(userState);
  const newCdArray = tableState.map((elParam: any) => elParam.cdSort);
  const cdSortArray = newCdArray.sort((a: number, b: number) => a - b);
  const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', title: '' });

  const [, drop] = useDrop({
    accept: 'ROW',
    hover(item: any, monitor) {
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }
      moveRow(tableState, setTableState, dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    drop: (item, monitor) => {
      const copyArray = [...tableState];
      const newArray = copyArray.map((cpItem, cpIndex) => ({ ...cpItem, cdSort: cdSortArray[cpIndex], editor: userInfo.userId }));
      setTableState(newArray);
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'ROW',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(trRef));

  const onChange = (tGubun: string, newValue: string) => {
    const updatedState = tableState.map((item: IEduexamT, i: number) => (i === index ? { ...item, [`${tGubun.toLocaleLowerCase()}Contents`]: newValue } : item));
    setTableState(updatedState);
  };

  const onClickRemoveInputContents = (e: React.MouseEvent<HTMLDivElement>, tGubun: string, dIndex: number) => {
    e.stopPropagation();
    const copyState = [...tableState];
    copyState[dIndex][`${tGubun.toLocaleLowerCase()}Contents`] = '';
    setTableState(copyState);
  };

  const onClickDeleteQuestion = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const target = tableState[index];
    if (target.tSeq) {
      if (type !== 'main' && target.tAnsYn) {
        toast.warning(t('정답은 삭제하실 수 없습니다'));
      } else {
        // 삭제
        setOpenModal((prev) => ({ ...prev, status: true, type: 'delete', api: () => deleteAPI(target) }));
      }
    } else {
      // 제거
      const copyState = [...tableState];
      copyState.splice(index, 1);
      setTableState(copyState);
    }
  };

  const deleteAPI = async (deleteObj: any) => {
    if (!selectedQuestionM) return;

    const newQuestionT = {
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      mSeq: selectedQuestionM.mSeq,
      mTitle: selectedQuestionM.mTitle,
      bigo: selectedQuestionM.bigo,
      useYn: selectedQuestionM?.useYn ? 'Y' : 'N',
      delYn: selectedQuestionM.delYn,
      writer: selectedQuestionM.writer,
      editor: userInfo.userId,
    };

    if (type === 'main') {
      const { tSeq, kContents, eContents, cContents, vContents, tContents, mContents, gContents, cdSort, writer } = tableState[index];
      const postDto = [
        {
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          mSeq: selectedQuestionM.mSeq,
          delYn: 'Y',
          tSeq,
          kContents,
          eContents,
          cContents,
          vContents,
          tContents,
          mContents,
          gContents,
          cdSort,
          writer,
          editor: userInfo.userId,
        },
      ];

      const req = { ...newQuestionT, [reqInfo.dtoKeyT]: postDto };
      const res = await apiPost({ path: reqInfo.path, req, contentType: 'application/json' });
      const { statusCode, message } = res.data;
      if (statusCode === 200) {
        let examMs = [];
        if (examType === 'SAFE_EDU') examMs = res.data.data.pmsEduexamMs;
        else examMs = res.data.data.pmsVisitexamMs;

        const { eDate, editor } = examMs[0];
        // 삭제하는 로우 제외하여 state 업데이트
        const result = tableState.filter((_: IEduexamT, i: number) => i !== index);
        setTableState(result);
        // 삭제하는 문제문항의 문제항목 목록이 노출되어있을 경우 삭제 후 null처리
        if (selectedQuestionT && selectedQuestionT.mSeq === selectedQuestionM.mSeq && tableState[index].tSeq === selectedQuestionT.tSeq) {
          setSelectedQuestionT(null);
        }
        // 수정자, 수정일자 업데이트
        setSelectedQuestionM((prev: IEduexamM) => ({ ...prev, eDate, editor }));
        refetchTList && refetchTList();
        toast.success(t(message));
        logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: examType === 'VISITOR' ? '방문자 설정 관리 > 문답 설정 (문제 문항)' : '태블릿 안전교육 관리 > 안전보건 교육 문제 관리 (문제 문항)',
          action: '삭제',
          etc: ``,
        });
      }
    } else {
      const copyState = [...tableState];
      copyState[index] = { ...tableState[index], delYn: 'Y', mSeq: selectedQuestionM.mSeq };
      const newArray = copyState.filter((item: IEduexamT) => item.delYn !== 'Y');

      // 삭제하는 항목를 제외함으로써 업데이트된 정답인덱스 찾기
      const ansNum = newArray.findIndex((item: IEduexamT) => item.tAnsYn);

      if (ansNum !== -1) {
        const ansNumToString = ansNum.toString();
        const removeTuseYnArray = copyState.map(({ expand, wDate, eDate, tAnsnum, languageYn, tAnsYn, ...rest }: IEduexamT) => rest);
        // explain 값 추가
        const req = {
          tAnsnum: ansNumToString,
          [reqInfo.dtoKeyD]: removeTuseYnArray,
          ...(examType === 'VISITOR' && {
            kExplain: explain.kExplain,
            eExplain: explain.eExplain,
            cExplain: explain.cExplain,
            vExplain: explain.vExplain,
            tExplain: explain.tExplain,
            mExplain: explain.mExplain,
            gExplain: explain.gExplain,
          }),
        };
        const res = await apiPost({ path: reqInfo.pathD, req, contentType: 'application/json' });

        const { statusCode, message, data } = res.data;
        if (statusCode === 200) {
          const result = data.filter((item: IEduexamT) => item.delYn !== 'Y');
          const array = updateAnswer(result, ansNumToString, excludeLanguages); // 정답 업데이트
          refetchTList && refetchTList(); // 문제문항 목록 리패치
          setSelectedQuestionT((prev: IEduexamT) => ({ ...prev, tAnsnum: ansNumToString }));
          setTableState(array);

          toast.success(t(message));
          logPost({
            hCd: userInfo.hCd,
            sCd: userInfo.sCd,
            userId: userInfo.userId,
            menu: examType === 'VISITOR' ? '방문자 설정 관리 > 문답 설정 (문제 항목)' : '태블릿 안전교육 관리 > 안전보건 교육 문제 관리 (문제 항목)',
            action: '삭제',
            etc: ``,
          });
        }
      } else toast.warning(t('정답은 삭제하실 수 없습니다'));
    }
  };

  const expandMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const newArray = tableState.map((item: IEduexamT, i: number) => (i === index ? { ...item, expand: !item.expand } : item));
    setTableState(newArray);
  };

  const handleUseYnClick = (e: React.MouseEvent<HTMLDivElement>, check: boolean, i: number) => {
    e.preventDefault();
    e.stopPropagation();
    const updatedCheckValue = !check;
    const copyArray = [...tableState];
    if (updatedCheckValue) {
      const newArray = copyArray.map((viewEl, viewIndex) => {
        return viewIndex === i ? { ...viewEl, tAnsYn: updatedCheckValue } : { ...viewEl, tAnsYn: !updatedCheckValue };
      });
      setTableState(newArray);
    }
  };
  return (
    <React.Fragment key={`${el.cdSort}_${index}`}>
      <ExpandMenuStyle ref={trRef} onClick={onClickQuestionRow} role='presentation' style={{ opacity: isDragging ? 0 : 1 }}>
        <MainMenuStyle>
          <ExpandButton isExpanded={el.expand} onClickExpandButton={(e) => expandMenu(e)} className='margin-05' />
          {type === 'sub' && (
            <ClickableContainer
              className='padding-05'
              onClick={(e: any) => handleUseYnClick(e, el.tAnsYn, index)} //
              button={<TableLamp use={el.tAnsYn} />}
            />
          )}
          <span className='question_num black'>{index + 1}</span>
          <QuestionRow
            key={`${el.tSeq}_K`}
            tGubun='K'
            languageType='한국어'
            tContents={el.kContents}
            onChangeQuestion={(e: React.ChangeEvent<HTMLInputElement>) => onChange('K', e.target.value)}
            onClickRemoveInputContents={(e: React.MouseEvent<HTMLDivElement>) => onClickRemoveInputContents(e, 'K', index)}
            onClickDeleteQuestion={(e: React.MouseEvent<HTMLDivElement>) => onClickDeleteQuestion(e)}
            auth={auth}
            tIndex={index}
            qIndex={index}
            type={type}
            examType={examType}
            paddingLeft={type === 'main' ? '5.85rem' : '8.35rem'}
          />
        </MainMenuStyle>
      </ExpandMenuStyle>
      {el.expand && requiredLanguage && (
        <>
          {requiredLanguage
            .filter((lan: IlanYn) => lan.value)
            .map(({ key, name }: IlanYn) => {
              if (key === 'K') return null;

              // 언어 로우가 표시되어야 하는지 여부를 결정
              const isLanguageEnabled = requiredLanguage?.some((item: IlanYn) => item.key === key && item.value);

              // 언어가 활성화된 경우 'sub' 유형에 대한 QuestionRow만 렌더링, main의 경우 항상 렌더링
              if (type === 'sub' && !isLanguageEnabled) return null;

              return (
                <QuestionRow
                  key={`${el.tSeq}_${key}`}
                  tGubun={key}
                  languageType={name}
                  tContents={el[`${key.toLowerCase()}Contents`]}
                  onChangeQuestion={(e: any) => onChange(key, e.target.value)}
                  onClickRemoveInputContents={(e: React.MouseEvent<HTMLDivElement>) => onClickRemoveInputContents(e, key, index)}
                  onClickDeleteQuestion={(e: React.MouseEvent<HTMLDivElement>) => onClickDeleteQuestion(e)}
                  auth={auth}
                  tIndex={index}
                  qIndex={index}
                  type={type}
                  examType={examType}
                  paddingLeft={type === 'main' ? '5.85rem' : '8.35rem'}
                />
              );
            })}
        </>
      )}
      {openModal.status && (
        <Portal openModal={openModal.status}>
          <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />
        </Portal>
      )}
    </React.Fragment>
  );
};

interface IQuestionRow {
  tGubun: string;
  languageType: string | undefined;
  tContents: string;
  onChangeQuestion: any;
  onClickDeleteQuestion: (e: React.MouseEvent<HTMLDivElement>, param1: number) => void;
  onClickRemoveInputContents: (e: React.MouseEvent<HTMLDivElement>, param1: number, param2: number) => void;
  auth: IAuth;
  tIndex: number;
  qIndex: number;
  type: 'main' | 'sub';
  examType: 'SAFE_EDU' | 'VISITOR';
  paddingLeft?: string;
  canDelete?: boolean;
}

interface IExplanationRow {
  tGubun: string;
  languageType: string | undefined;
  tContents: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>, tGubun: string) => void;
  paddingLeft?: string;
}

const QuestionRow = ({
  tGubun,
  languageType,
  tContents,
  onChangeQuestion,
  onClickRemoveInputContents,
  onClickDeleteQuestion,
  auth,
  tIndex,
  qIndex,
  type,
  examType,
  paddingLeft,
  canDelete = true,
}: IQuestionRow) => {
  return (
    <SubMenuListStyle key={tGubun} className='mainMenuContainer' style={{ paddingLeft: `${tGubun !== 'K' ? `${paddingLeft}` : ''}` }}>
      <div className={`languageType question_num ${tGubun !== 'K' ? 'violet' : 'blue'}`}>{languageType}</div>
      <input className='namespace' type='text' name='tContents' value={tContents} onChange={(e) => onChangeQuestion(e, tIndex, qIndex)} maxLength={200} />
      {auth.deleteAuth &&
        canDelete &&
        (tGubun === 'K' ? (
          <div className='button delete-button' role='presentation' onClick={(e) => onClickDeleteQuestion(e, tIndex)}>
            <span className='material-symbols-rounded'>delete</span>
          </div>
        ) : (
          <div className='button default-button' role='presentation' onClick={(e) => onClickRemoveInputContents(e, tIndex, qIndex)}>
            <span className='material-symbols-rounded'>backspace</span>
          </div>
        ))}
    </SubMenuListStyle>
  );
};

const ExplanationRow = ({ tGubun, languageType, tContents, onChange, paddingLeft }: IExplanationRow) => {
  return (
    <SubMenuListStyle key={tGubun} className='mainMenuContainer' style={{ paddingLeft: `${tGubun !== 'K' ? `${paddingLeft}` : ''}` }}>
      <div className={`languageType question_num ${tGubun !== 'K' ? 'violet' : 'blue'}`}>{languageType}</div>
      <textarea className='namespace' name='tContents' value={tContents} onChange={(e) => onChange(e, tGubun)} maxLength={500} />
    </SubMenuListStyle>
  );
};

const dragRow = (list: any[], setList: Dispatch<SetStateAction<any[]>>, fromIndex: number, toIndex: number) => {
  const updatedData = [...list];
  const [movedItem] = updatedData.splice(fromIndex, 1);
  updatedData.splice(toIndex, 0, movedItem);
  setList(updatedData);
};

export default SafeEduExamSetting;
